import * as React from 'react';

import { cn } from '@eluve/components';

// The figure/ground kit for the care-plan render primitives: card surfaces
// (Panel) on the canvas, a quieter inset surface for internal contrast
// (PanelInset), and the small recurring objects that live on them (IconTile,
// Eyebrow, MonoChip, SectionHeader). Pair with an accent from `./accents` —
// e.g. `<Panel className={accent.edgeL}>`, `<IconTile className={accent.tile}>`.

/** A distinct surface object on the canvas: card tone, real border, soft shadow. */
export function Panel({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="panel"
      className={cn(
        'rounded-xl border border-border bg-card text-card-foreground shadow-sm',
        className,
      )}
      {...props}
    />
  );
}

/** Panel's quieter sibling: an inset tone for internal contrast, borderless so
 * it reads as recessed. */
export function PanelInset({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="panel-inset"
      className={cn(
        'rounded-xl bg-bg-tertiary text-foreground shadow-xs',
        className,
      )}
      {...props}
    />
  );
}

/** A tinted square holding an icon (or a number) — give it an accent's `tile`. */
export function IconTile({
  className,
  size = 'default',
  ...props
}: React.ComponentProps<'span'> & { size?: 'default' | 'sm' }) {
  return (
    <span
      data-slot="icon-tile"
      data-size={size}
      className={cn(
        'grid size-9 shrink-0 place-items-center rounded-lg data-[size=sm]:size-7 data-[size=sm]:rounded-md [&_svg]:size-4 data-[size=sm]:[&_svg]:size-3.5',
        className,
      )}
      {...props}
    />
  );
}

/** Uppercase micro-label. Muted by default; pass an accent's `text` to color it. */
export function Eyebrow({ className, ...props }: React.ComponentProps<'span'>) {
  return (
    <span
      data-slot="eyebrow"
      className={cn(
        'text-[10px] font-semibold tracking-widest text-muted-foreground uppercase',
        className,
      )}
      {...props}
    />
  );
}

/** Monospace value chip for metrics/durations. Neutral by default; pass an
 * accent's `tile` to tint it. */
export function MonoChip({
  className,
  ...props
}: React.ComponentProps<'span'>) {
  return (
    <span
      data-slot="mono-chip"
      className={cn(
        'inline-flex w-fit items-center gap-1 rounded-md border border-border bg-muted px-2 py-0.5 font-mono text-[11px] text-muted-foreground [&_svg]:size-3',
        className,
      )}
      {...props}
    />
  );
}

/** Section heading with an accent bar: the canvas-level grouping device. */
export function SectionHeader({
  title,
  description,
  barClassName,
  className,
  ...props
}: React.ComponentProps<'div'> & {
  title: React.ReactNode;
  description?: React.ReactNode;
  barClassName?: string;
}) {
  return (
    <div
      data-slot="section-header"
      className={cn('flex gap-2.5', className)}
      {...props}
    >
      <span
        aria-hidden
        className={cn(
          'mt-1 h-4 w-1 shrink-0 rounded-full bg-bg-quaternary',
          barClassName,
        )}
      />
      <div className="min-w-0">
        <h2 className="text-md font-semibold text-foreground">{title}</h2>
        {description ? (
          <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>
        ) : null}
      </div>
    </div>
  );
}
